import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LoginAttempt } from '../models/loginAttempt';
import { PaginatedResponse } from '../models/paginatedResponse';
import { CreateLoginAttemptRequest } from '../models/api/createLoginAttemptRequest';
import { LoginAttemptSearchRequest } from '../models/api/loginAttemptSearchRequest';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LoginAttemptService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/LoginAttempts`;

  /**
   * POST /api/LoginAttempts
   * Create a new login attempt
   */
  createLoginAttempt(request: CreateLoginAttemptRequest): Observable<any> {
    return this.http.post(this.apiUrl, request);
  }

  /**
   * GET /api/LoginAttempts
   * Get paginated list of login attempts
   */
  getLoginAttempts(
    page: number = 1,
    pageSize: number = 20,
    sortBy: string = 'attemptDate',
    ascending: boolean = false
  ): Observable<PaginatedResponse<LoginAttempt>> {
    let params = new HttpParams()
      .set('pageNumber', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('ascending', ascending.toString());

    return this.http.get<PaginatedResponse<LoginAttempt>>(this.apiUrl, { params });
  }

  /**
   * POST /api/LoginAttempts/search
   * Search login attempts with filters, pagination and sorting
   */
  searchLoginAttempts(searchRequest: LoginAttemptSearchRequest): Observable<PaginatedResponse<LoginAttempt>> {
    return this.http.post<PaginatedResponse<LoginAttempt>>(`${this.apiUrl}/search`, searchRequest);
  }

  /**
   * GET /api/LoginAttempts/user/{username}
   * Get login attempts for a specific user
   */
  getLoginAttemptsByUsername(
    username: string,
    count?: number
  ): Observable<LoginAttempt[]> {
    let params = new HttpParams();

    if (count !== undefined) {
      params = params.set('count', count.toString());
    }

    return this.http.get<LoginAttempt[]>(`${this.apiUrl}/user/${username}`, { params });
  }

  /**
   * GET /api/LoginAttempts/user/{username}/failed
   * Get failed login attempts for a specific user within a time window
   */
  getFailedLoginAttempts(
    username: string,
    timeWindowMinutes: number = 30
  ): Observable<LoginAttempt[]> {
    let params = new HttpParams()
      .set('timeWindowMinutes', timeWindowMinutes.toString());

    return this.http.get<LoginAttempt[]>(`${this.apiUrl}/user/${username}/failed`, { params });
  }

  /**
   * GET /api/LoginAttempts/throttle/{username}
   * Check throttle status for a user
   */
  getThrottleStatus(
    username: string,
    ipAddress?: string
  ): Observable<any> {
    let params = new HttpParams();

    if (ipAddress) {
      params = params.set('ipAddress', ipAddress);
    }

    return this.http.get(`${this.apiUrl}/throttle/${username}`, { params });
  }

  /**
   * POST /api/LoginAttempts/throttle/{username}/reset
   * Reset throttle for a user
   */
  resetThrottle(
    username: string,
    ipAddress?: string
  ): Observable<any> {
    let params = new HttpParams();

    if (ipAddress) {
      params = params.set('ipAddress', ipAddress);
    }

    return this.http.post(`${this.apiUrl}/throttle/${username}/reset`, null, { params });
  }
}
